import React, { useState, useRef, useEffect } from 'react';

import { Text, ArrowDown, ArrowUp } from '@doda/io-react/common';
import classNames from 'classnames';
import { JobWorkLocationResponse } from '@doda/api-client/aspida/@types';
import { TextWithLF } from '@doda/common/src/components/TextWithLF';
import { SectionItem } from '../../SectionItem';
import styles from './index.module.scss';

/**
 * LocationコンポーネントのProps
 */
interface LocationProps {
  // 勤務地の詳細
  jobState: string | undefined;
  // 具体的な勤務地の配列
  workLocations: JobWorkLocationResponse[];
}

/**
 * 職務地の詳細を表示するコンポーネント
 */
export const Location: React.FC<LocationProps> = ({
  jobState,
  workLocations,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenExtra, setIsOpenExtra] = useState(false);
  const ref = useRef<HTMLUListElement | null>(null);
  const extraRef = useRef<HTMLDivElement | null>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [extraButtonPosition, setExtraButtonPosition] = useState(0);
  const hasExtra = containerHeight > 480;

  useEffect(() => {
    if (ref.current) {
      setContainerHeight(ref.current.offsetHeight);
    }
    if (extraRef.current) {
      setExtraButtonPosition(
        extraRef.current.getBoundingClientRect().top + window.scrollY
      );
    }
  }, []);

  // 勤務地リストを表示または非表示にするための状態を切り替え
  const handleClickMore = () => {
    setIsOpen((prevState) => !prevState);
  };

  // 追加の勤務地を表示または非表示にするための状態を切り替え
  const handleClickExtraMore = () => {
    if (isOpenExtra) {
      window.scrollTo(0, extraButtonPosition);
    }
    setIsOpenExtra((prevState) => !prevState);
  };

  return (
    <SectionItem label="勤務地">
      {jobState && <TextWithLF text={jobState} />}
      {workLocations.length !== 0 && (
        <div ref={extraRef}>
          {/* 勤務地リストのトグル */}
          <Text
            as="button"
            color="blue"
            size="14--articlePC"
            weight="bold"
            style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}
            onClick={handleClickMore}
          >
            勤務地一覧
            {isOpen ? (
              <>
                を閉じる
                <ArrowUp size="XS" color="blue" />
              </>
            ) : (
              <>
                （{workLocations.length}件）を見る
                <ArrowDown size="XS" color="blue" />
              </>
            )}
          </Text>
          {/* 勤務地リストコンテナ */}
          <div
            className={classNames(
              styles['jobSearchDetail-location__listWrapper'],
              {
                [styles['jobSearchDetail-location__listWrapper--open']]: isOpen,
                [styles['jobSearchDetail-location__listWrapper--openExtra']]:
                  isOpenExtra,
                [styles['jobSearchDetail-location__listWrapper--closeExtra']]:
                  !isOpenExtra && hasExtra,
              }
            )}
            style={{
              maxHeight:
                isOpenExtra && isOpen ? `${containerHeight}px` : undefined,
            }}
          >
            {/* 勤務地リスト */}
            <ul ref={ref} className={styles['jobSearchDetail-location__list']}>
              {workLocations.map((location, index) => (
                <LocationListItem key={index} location={location} />
              ))}
            </ul>
            {/* 勤務地が多い場合のトグル */}
            {hasExtra && (
              <div
                className={classNames(
                  styles['jobSearchDetail-location__layer'],
                  {
                    [styles['jobSearchDetail-location__layer--openExtra']]:
                      isOpenExtra,
                  }
                )}
              >
                <Text
                  as="button"
                  color="blue"
                  size="14--articlePC"
                  weight="bold"
                  style={{
                    display: 'flex',
                    position: isOpenExtra ? 'absolute' : undefined,
                    bottom: '0px',
                  }}
                  onClick={handleClickExtraMore}
                >
                  {isOpenExtra ? (
                    <>
                      閉じる
                      <ArrowUp size="XS" color="blue" />
                    </>
                  ) : (
                    <>
                      続きを見る
                      <ArrowDown size="XS" color="blue" />
                    </>
                  )}
                </Text>
              </div>
            )}
          </div>
        </div>
      )}
    </SectionItem>
  );
};

/**
 * LocationListItemのprops
 */
interface LocationListItemProps {
  location: JobWorkLocationResponse;
}

const LocationListItem: React.FC<LocationListItemProps> = ({ location }) => (
  <li className={styles['jobSearchDetail-location__listItem']}>
    {location.wofcstnm && `${location.wofcstnm}/`}
    {location.wfwad}
    <span className={styles['jobSearchDetail-location__listStation']}>
      [最寄り駅]{location.wdspstnnm}
      {location.wnrst && ` ${location.wnrst}`}
    </span>
  </li>
);
